<script setup lang="ts">
import { ref } from 'vue'
import { Plus } from 'lucide-vue-next'
import { ActionItem, ActionsGroup, ActionsMenu } from '@/modules/shared/components'
import { useBuilderStore } from '../stores/builder-store'
import type { ApplicationQuestion, QuestionType } from '../types'

// The 1-based slot the new question takes: the button above the list passes 1
// (insert at top), the button after a question passes that question's
// position + 1 (insert directly below it).
const props = defineProps<{ headingId: number; position?: number }>()
const emit = defineEmits<{ created: [question: ApplicationQuestion] }>()
const builderStore = useBuilderStore()

const creating = ref(false)
// The shared ActionsMenu has no close-on-select; bumping this key remounts it
// the instant an option is chosen, which closes the dropdown immediately.
const menuKey = ref(0)

// The four types the legacy builder offered outside dev builds. matrix_table
// exists in the data model's enum, but the taking flow never implemented
// answering it (empty response branch, "implement matrix tables later" in the
// response cache) and creation was dev-only — so it is not offered here.
const typeLabels: { type: QuestionType; label: string }[] = [
  { type: 'short_answer', label: 'Short Answer' },
  { type: 'multiple_choice', label: 'Multiple Choice' },
  { type: 'boolean', label: 'Yes / No' },
  { type: 'file_upload', label: 'File Upload' },
]

const create = async (type: QuestionType) => {
  if (creating.value) return

  menuKey.value += 1
  creating.value = true
  try {
    const question = await builderStore.createQuestion(props.headingId, 'Untitled Question', type, props.position)
    emit('created', question)
  } catch {
    // error already surfaced via { silent: false }
  } finally {
    creating.value = false
  }
}
</script>

<template>
  <!-- Legacy pattern: a circular plus button sitting on a divider, opening the
       question-type dropdown (applications/questions/_create_button.html.erb). -->
  <div class="relative my-6 border-t-2 border-gray-100">
    <div class="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
      <ActionsMenu :key="menuKey" class="plus-trigger">
        <template #menu_button>
          <span
            class="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            title="Create a New Question"
          >
            <Plus class="h-4 w-4" />
          </span>
        </template>
        <ActionsGroup>
          <ActionItem
            v-for="option in typeLabels"
            :key="option.type"
            :text="option.label"
            @click="create(option.type)"
          />
        </ActionsGroup>
      </ActionsMenu>
    </div>
  </div>
</template>

<style scoped>
/* The shared ActionsMenu wraps its trigger in a white, shadowed button; the
   builder's plus is a bare circle sitting on the divider, so neutralize the
   wrapper via this opt-in class instead of changing the shared component. */
.plus-trigger :deep(button) {
  background-color: transparent;
  box-shadow: none;
}

.plus-trigger :deep(button:hover) {
  background-color: transparent;
}
</style>
